import { FIELDS, FIELD_KEYS, type FieldDef } from './fields'
import type { ColumnMapping, MappingSuggestion } from './types'

/**
 * Práh, pod kterým se návrh NEPŘEDVYPLNÍ.
 *
 * Vědomé rozhodnutí: prázdné pole, které uživatel vidí a vyplní, je levnější
 * než tiše špatně namapovaný sloupec, který se projeví až u brány.
 */
export const SUGGESTION_THRESHOLD = 0.7

type Scored = { field: string; column: number; score: number; reason: MappingSuggestion['reason'] }

/** Pole nesoucí ID, ne název — u nich je fuzzy shoda zrádná (viz scoreColumn). */
const ID_FIELDS = new Set(['ticketsetId', 'priceId'])

/** Zmiňuje název sloupce ID? Po normalizaci (bez mezer a diakritiky). */
const MENTIONS_ID = /id/

/**
 * Navrhne mapování sloupců na cílová pole.
 *
 * Tři nezávislé signály (alias / fuzzy název / obsah) plus poziční fallback pro
 * bezhlavičkové soubory v kanonickém pořadí. Každý sloupec smí být přiřazen
 * nejvýš jednomu poli — kolize řeší vyšší skóre.
 */
export function suggestMapping(
    rows: string[][],
    headers: string[] | null
): Record<string, MappingSuggestion> {
    const columnCount = rows.reduce((max, r) => Math.max(max, r.length), headers?.length ?? 0)

    const candidates: Scored[] = []
    for (const field of FIELDS) {
        for (let c = 0; c < columnCount; c++) {
            const scored = scoreColumn(field, c, rows, headers, columnCount)
            if (scored.score > 0) candidates.push({ field: field.key, column: c, ...scored })
        }
    }

    // Greedy podle skóre: nejjistější dvojice si sloupec zabere první.
    candidates.sort((a, b) => b.score - a.score)

    const takenColumns = new Set<number>()
    const assigned = new Map<string, Scored>()
    for (const cand of candidates) {
        if (assigned.has(cand.field) || takenColumns.has(cand.column)) continue
        if (cand.score < SUGGESTION_THRESHOLD) continue
        assigned.set(cand.field, cand)
        takenColumns.add(cand.column)
    }

    const out: Record<string, MappingSuggestion> = {}
    for (const key of FIELD_KEYS) {
        const hit = assigned.get(key)
        out[key] = hit
            ? { source: { kind: 'column', index: hit.column }, score: hit.score, reason: hit.reason }
            : { source: { kind: 'empty' }, score: 0, reason: 'none' }
    }
    return out
}

/** Návrh → rovnou použitelné mapování. */
export function suggestionsToMapping(suggestions: Record<string, MappingSuggestion>): ColumnMapping {
    const mapping: ColumnMapping = {}
    for (const [key, s] of Object.entries(suggestions)) mapping[key] = s.source
    return mapping
}

function scoreColumn(
    field: FieldDef,
    column: number,
    rows: string[][],
    headers: string[] | null,
    columnCount: number
): { score: number; reason: MappingSuggestion['reason'] } {
    const header = headers?.[column]

    if (header) {
        const norm = normalise(header)

        if (field.aliases.some(a => normalise(a) === norm)) {
            return { score: 1, reason: 'alias' }
        }

        const fuzzy = Math.max(...field.aliases.map(a => similarity(normalise(a), norm)))
        if (fuzzy >= 0.75) {
            // ID pole („ticketsetId“) jsou textově blízko svým jmenným
            // protějškům („ticketsetName“), takže by fuzzy shoda sebrala sloupec
            // jako „ticket set“ ID poli a název by zůstal prázdný. Bez doslovné
            // zmínky o ID se proto skóre ID polí srazí pod práh.
            if (ID_FIELDS.has(field.key) && !MENTIONS_ID.test(norm)) {
                return { score: 0, reason: 'none' }
            }
            return { score: 0.5 + fuzzy * 0.4, reason: 'fuzzy' }
        }
    }

    const sample = rows.slice(0, 20).map(r => r[column] ?? '').filter(v => v.trim() !== '')
    const content = contentScore(field, sample)

    // Bez hlavičky je pořadí sloupců jediné vodítko navíc. Uplatní se jen když
    // šířka přesně odpovídá kanonickému formátu.
    if (!headers && columnCount === FIELD_KEYS.length && FIELD_KEYS[column] === field.key) {
        return { score: Math.max(content, 0.75), reason: content >= 0.75 ? 'content' : 'position' }
    }

    return { score: content, reason: content > 0 ? 'content' : 'none' }
}

/**
 * Jak moc vzorek hodnot odpovídá charakteru pole.
 *
 * Jediný signál, který funguje i u bezhlavičkových souborů.
 */
function contentScore(field: FieldDef, sample: string[]): number {
    if (sample.length === 0) return 0
    const ratio = (test: (v: string) => boolean) => sample.filter(test).length / sample.length

    switch (field.type) {
        case 'email': {
            const r = ratio(v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v))
            return r >= 0.8 ? 0.4 + r * 0.4 : 0
        }
        case 'date': {
            const r = ratio(isDateLike)
            return r >= 0.8 ? 0.4 + r * 0.4 : 0
        }
        case 'currency': {
            const r = ratio(v => /^[A-Za-z]{3}$/.test(v))
            return r >= 0.8 ? 0.4 + r * 0.4 : 0
        }
        case 'number': {
            const r = ratio(isNumeric)
            // Čísel je v souboru spousta — samo o sobě slabý signál, jinak by
            // se každý číselný sloupec hlásil o každé číselné pole.
            return r >= 0.9 ? 0.45 : 0
        }
        default:
            break
    }

    if (field.key === 'code') return codeScore(sample)
    if (field.key === 'phone') {
        const r = ratio(v => /^[+]?[\d\s()-]{6,}$/.test(v))
        return r >= 0.8 ? 0.4 + r * 0.4 : 0
    }
    return 0
}

/**
 * Kód vstupenky: dlouhý alfanumerický řetězec, který se skoro neopakuje.
 * Unikátnost je tu klíčová — odliší kód od kategorie nebo názvu.
 */
function codeScore(sample: string[]): number {
    const plausible = sample.filter(v => /^[A-Za-z0-9-]{6,16}$/.test(v))
    if (plausible.length / sample.length < 0.9) return 0

    const uniqueRatio = new Set(sample).size / sample.length
    if (uniqueRatio < 0.9) return 0

    return 0.55 + uniqueRatio * 0.25
}

function isDateLike(v: string): boolean {
    return /^\d{4}-\d{2}-\d{2}([ T]\d{1,2}:\d{2})?/.test(v) || /^\d{1,2}\.\s?\d{1,2}\.\s?\d{4}( \d{1,2}:\d{2})?/.test(v)
}

function isNumeric(v: string): boolean {
    return /^[+-]?[\d\s\u00a0]*[.,]?\d+$/.test(v)
}

/** Podobnost 0–1 přes Levenshteinovu vzdálenost. */
function similarity(a: string, b: string): number {
    if (!a || !b) return 0
    if (a === b) return 1
    const distance = levenshtein(a, b)
    return 1 - distance / Math.max(a.length, b.length)
}

function levenshtein(a: string, b: string): number {
    const prev = Array.from({ length: b.length + 1 }, (_, i) => i)
    for (let i = 1; i <= a.length; i++) {
        let diagonal = prev[0]
        prev[0] = i
        for (let j = 1; j <= b.length; j++) {
            const temp = prev[j]
            prev[j] = Math.min(
                prev[j] + 1,
                prev[j - 1] + 1,
                diagonal + (a[i - 1] === b[j - 1] ? 0 : 1)
            )
            diagonal = temp
        }
    }
    return prev[b.length]
}

function normalise(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
}
